// Extend Performance interface for memory property
declare global {
  interface Performance {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }
}

interface DragOperationMetrics {
  operationId: string;
  wallId: string;
  operationType: string;
  startTime: number;
  endTime?: number;
  events: Array<{
    eventType: string;
    timestamp: number;
    duration: number;
    memoryUsage?: number;
  }>;
  totalDuration?: number;
  errorCount: number;
  recoveryAttempts: number;
}

interface PerformanceThreshold {
  metric: string;
  threshold: number;
  severity: 'warning' | 'error' | 'critical';
  callback?: (value: number, threshold: number) => void;
}

interface PerformanceAlert {
  id: string;
  message: string;
  severity: 'warning' | 'error' | 'critical';
  timestamp: number;
  metric: string;
  value: number;
  threshold: number;
  wallId?: string;
  operationType?: string;
}

interface PerformanceMetrics {
  // Timing metrics
  operationDurations: Map<string, number[]>;
  averageFrameTime: number;
  frameRateHistory: number[];
  updateFrequencies: Map<string, number>;

  // Memory metrics
  memoryUsageHistory: number[];
  memoryLeakIndicators: Array<{ timestamp: number; usage: number }>;
  garbageCollectionImpact: number[];

  // Error metrics
  errorRates: Map<string, { count: number; total: number }>;
  errorRecoveryTimes: number[];
  criticalErrors: number;

  // User experience metrics
  responsivenessFactor: number;
  visualGlitches: number;
  performanceDegradation: number;
}

class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private isEnabled = false;
  private memoryTrackingTimeout: number | null = null;
  private isTracking = false;

  // Operation tracking
  private dragOperations = new Map<string, DragOperationMetrics>();
  private activeOperations = new Set<string>();
  private operationCounter = 0;

  // Performance metrics
  private metrics: PerformanceMetrics = {
    operationDurations: new Map(),
    averageFrameTime: 0,
    frameRateHistory: [],
    updateFrequencies: new Map(),
    memoryUsageHistory: [],
    memoryLeakIndicators: [],
    garbageCollectionImpact: [],
    errorRates: new Map(),
    errorRecoveryTimes: [],
    criticalErrors: 0,
    responsivenessFactor: 1.0,
    visualGlitches: 0,
    performanceDegradation: 0
  };

  // Thresholds and alerts
  private thresholds = new Map<string, PerformanceThreshold>();
  private alerts = new Map<string, PerformanceAlert>();
  private alertCallbacks = new Set<(alert: PerformanceAlert) => void>();
  private lastAlertTimes = new Map<string, number>();

  // Analysis data
  private analysisHistory: Array<{ timestamp: number; analysis: any }> = [];
  private lastFrameTime = performance.now();
  private frameCount = 0;

  constructor() {
    this.initializeDefaultThresholds();
    // Disabled by default to prevent infinite loop and browser hang
    // this.startPerformanceTracking();
  }

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  enableMonitoring(): void {
    this.isEnabled = true;
    this.startPerformanceTracking();
  }

  disableMonitoring(): void {
    this.isEnabled = false;
    this.isTracking = false;
    // Clear any pending memory tracking timeout
    if (this.memoryTrackingTimeout !== null) {
      clearTimeout(this.memoryTrackingTimeout);
      this.memoryTrackingTimeout = null;
    }
  }

  private initializeDefaultThresholds() {
    // Drag operation thresholds (in milliseconds) - adjusted for complex 3D operations
    this.setPerformanceThreshold('dragOperationDuration', 2000, 'warning');
    this.setPerformanceThreshold('dragOperationDuration', 5000, 'error');
    this.setPerformanceThreshold('dragOperationDuration', 10000, 'critical');

    // Frame rate thresholds
    this.setPerformanceThreshold('frameRate', 30, 'warning');
    this.setPerformanceThreshold('frameRate', 20, 'error');
    this.setPerformanceThreshold('frameRate', 10, 'critical');

    // Memory thresholds (in MB) - adjusted for complex 3D floor planning application
    this.setPerformanceThreshold('memoryUsage', 2000, 'warning');
    this.setPerformanceThreshold('memoryUsage', 3000, 'error');
    this.setPerformanceThreshold('memoryUsage', 4000, 'critical');

    // Error rate thresholds (percentage)
    this.setPerformanceThreshold('errorRate', 5, 'warning');
    this.setPerformanceThreshold('errorRate', 10, 'error');
    this.setPerformanceThreshold('errorRate', 20, 'critical');
  }

  private startPerformanceTracking() {
    // Safety check - don't start if already disabled or already tracking
    if (!this.isEnabled || this.isTracking) return;
    
    this.isTracking = true;

    // Skip frame rate tracking for demand-based rendering (frameloop="demand")
    // This prevents false FPS warnings when the app only renders on invalidate()

    // Track memory usage every 5 seconds
    const trackMemory = () => {
      if (performance.memory && this.isEnabled) {
        const memoryUsage = performance.memory.usedJSHeapSize / (1024 * 1024); // MB
        this.metrics.memoryUsageHistory.push(memoryUsage);

        // Keep only last 100 measurements
        if (this.metrics.memoryUsageHistory.length > 100) {
          this.metrics.memoryUsageHistory.shift();
        }

        // Detect potential memory leaks
        if (this.metrics.memoryUsageHistory.length >= 10) {
          const recent = this.metrics.memoryUsageHistory.slice(-10);
          const trend = this.calculateTrend(recent);

          if (trend > 1.0) { // Memory consistently increasing
            this.metrics.memoryLeakIndicators.push({
              timestamp: Date.now(),
              usage: memoryUsage
            });
          }
        }

        // Check memory thresholds
        this.checkThresholds('memoryUsage', memoryUsage);

        // Schedule next memory check only if monitoring is still enabled
        if (this.isEnabled) {
          this.memoryTrackingTimeout = window.setTimeout(trackMemory, 5000);
        }
      }
    };

    this.memoryTrackingTimeout = window.setTimeout(trackMemory, 5000);
  }

  startDragOperation(wallId: string, operationType: string): string {
    if (!this.isEnabled) return '';

    const operationId = `drag_${++this.operationCounter}_${Date.now()}`;
    const startTime = performance.now();

    const operation: DragOperationMetrics = {
      operationId,
      wallId,
      operationType,
      startTime,
      events: [],
      errorCount: 0,
      recoveryAttempts: 0
    };

    this.dragOperations.set(operationId, operation);
    this.activeOperations.add(operationId);

    // Track memory at operation start
    if (performance.memory) {
      operation.events.push({
        eventType: 'start',
        timestamp: startTime,
        duration: 0,
        memoryUsage: performance.memory.usedJSHeapSize / (1024 * 1024)
      });
    }

    return operationId;
  }

  endDragOperation(operationId: string): void {
    if (!this.isEnabled || !this.dragOperations.has(operationId)) return;

    const operation = this.dragOperations.get(operationId)!;
    const endTime = performance.now();
    const totalDuration = endTime - operation.startTime;

    operation.endTime = endTime;
    operation.totalDuration = totalDuration;
    this.activeOperations.delete(operationId);

    // Track memory at operation end
    if (performance.memory) {
      operation.events.push({
        eventType: 'end',
        timestamp: endTime,
        duration: totalDuration,
        memoryUsage: performance.memory.usedJSHeapSize / (1024 * 1024)
      });
    }

    // Update operation duration metrics
    const operationKey = `${operation.operationType}_${operation.wallId}`;
    if (!this.metrics.operationDurations.has(operationKey)) {
      this.metrics.operationDurations.set(operationKey, []);
    }
    this.metrics.operationDurations.get(operationKey)!.push(totalDuration);

    // Check duration thresholds
    this.checkThresholds('dragOperationDuration', totalDuration, operation.wallId, operation.operationType);

    // Clean up old operations (keep last 100)
    if (this.dragOperations.size > 100) {
      const oldestKey = this.dragOperations.keys().next().value;
      if (oldestKey !== undefined) {
        this.dragOperations.delete(oldestKey);
      }
    }
  }

  trackDragEvent(wallId: string, eventType: string, duration: number): void {
    if (!this.isEnabled) return;

    // Find active operation for this wall
    for (const [operationId, operation] of this.dragOperations) {
      if (operation.wallId === wallId && this.activeOperations.has(operationId)) {
        operation.events.push({
          eventType,
          timestamp: performance.now(),
          duration,
          memoryUsage: performance.memory ? performance.memory.usedJSHeapSize / (1024 * 1024) : undefined
        });
        break;
      }
    }
  }

  trackError(wallId: string, operationType: string, errorType: string): void {
    if (!this.isEnabled) return;

    const errorKey = `${operationType}_${errorType}`;

    if (!this.metrics.errorRates.has(errorKey)) {
      this.metrics.errorRates.set(errorKey, { count: 0, total: 0 });
    }

    const errorData = this.metrics.errorRates.get(errorKey)!;
    errorData.count++;
    errorData.total++;

    // Track critical errors
    if (errorType === 'critical' || errorType === 'memory') {
      this.metrics.criticalErrors++;
    }

    // Update error count in active operations
    for (const [operationId, operation] of this.dragOperations) {
      if (operation.wallId === wallId && this.activeOperations.has(operationId)) {
        operation.errorCount++;
        break;
      }
    }

    // Calculate error rate and check thresholds
    const errorRate = (errorData.count / errorData.total) * 100;
    this.checkThresholds('errorRate', errorRate, wallId, operationType, errorData.total);
  }

  trackRecovery(wallId: string, operationType: string, recoveryTime: number): void {
    if (!this.isEnabled) return;

    this.metrics.errorRecoveryTimes.push(recoveryTime);

    // Update recovery attempts in active operations
    for (const [operationId, operation] of this.dragOperations) {
      if (operation.wallId === wallId && this.activeOperations.has(operationId)) {
        operation.recoveryAttempts++;
        break;
      }
    }

    // Keep only last 100 recovery times
    if (this.metrics.errorRecoveryTimes.length > 100) {
      this.metrics.errorRecoveryTimes.shift();
    }
  }

  /**
   * Track drag-specific performance metrics
   */
  trackDragPerformance(wallId: string, operation: string, startTime: number): void {
    if (!this.isEnabled) return;

    const duration = performance.now() - startTime;

    // Find active operation for this wall
    for (const [operationId, operation] of this.dragOperations) {
      if (operation.wallId === wallId && this.activeOperations.has(operationId)) {
        operation.events.push({
          eventType: `drag_${operation}`,
          timestamp: performance.now(),
          duration,
          memoryUsage: performance.memory ? performance.memory.usedJSHeapSize / (1024 * 1024) : undefined
        });
        break;
      }
    }
  }

  /**
   * Get drag-specific performance metrics
   */
  getDragPerformanceMetrics(): object {
    if (!this.isEnabled) return {};

    const metrics: any = {
      activeOperations: this.activeOperations.size,
      totalOperations: this.dragOperations.size,
      averageFrameTime: this.metrics.averageFrameTime,
      frameRateHistory: this.metrics.frameRateHistory.slice(-10) // Last 10 frames
    };

    // Calculate average drag operation time
    const dragEvents = Array.from(this.dragOperations.values())
      .flatMap(op => op.events.filter(event => event.eventType.startsWith('drag_')));

    if (dragEvents.length > 0) {
      metrics.averageDragTime = dragEvents.reduce((sum, event) => sum + (event.duration || 0), 0) / dragEvents.length;
    }

    return metrics;
  }

  /**
   * Determine if attachment indicators should be shown based on performance
   */
  shouldShowAttachmentIndicators(): boolean {
    if (!this.isEnabled) return false;

    // Check frame rate - disable indicators if below 30fps
    if (this.metrics.averageFrameTime > 33) { // 33ms = ~30fps
      return false;
    }

    // Check if there are too many active operations
    if (this.activeOperations.size > 5) {
      return false;
    }

    // Check memory usage - disable if memory is high
    if (performance.memory && performance.memory.usedJSHeapSize > 500 * 1024 * 1024) { // 500MB
      return false;
    }

    return true;
  }

  setPerformanceThreshold(metric: string, threshold: number, severity: 'warning' | 'error' | 'critical', callback?: (value: number, threshold: number) => void): void {
    const key = `${metric}_${severity}`;
    this.thresholds.set(key, { metric, threshold, severity, callback });
  }

  private checkThresholds(metric: string, value: number, wallId?: string, operationType?: string, sampleSize: number = Number.POSITIVE_INFINITY): void {
    if (metric === 'errorRate' && sampleSize < 5) {
      return;
    }

    const severityRank: Record<PerformanceThreshold['severity'], number> = {
      warning: 1,
      error: 2,
      critical: 3,
    };
    let triggeredThreshold: PerformanceThreshold | undefined;

    for (const threshold of this.thresholds.values()) {
      if (threshold.metric === metric) {
        // For frameRate: warning/error/critical when value < threshold (lower is worse)
        // For other metrics: keep current logic where value > threshold (higher is worse)
        const shouldTriggerAlert = metric === 'frameRate' ?
          value < threshold.threshold :
          value > threshold.threshold;

        if (shouldTriggerAlert && (!triggeredThreshold || severityRank[threshold.severity] > severityRank[triggeredThreshold.severity])) {
          triggeredThreshold = threshold;
        }
      }
    }

    if (!triggeredThreshold) {
      return;
    }

    // Add rate limiting for alerts to prevent spam
    const now = Date.now();
    const lastAlertKey = `${metric}_${triggeredThreshold.severity}_lastAlert`;
    const lastAlertTime = this.lastAlertTimes.get(lastAlertKey) ?? 0;
    const minAlertInterval = metric === 'errorRate'
      ? 30000
      : triggeredThreshold.severity === 'critical'
        ? 1000 // 1 second for critical
        : triggeredThreshold.severity === 'error'
          ? 5000 // 5 seconds for errors
          : 10000; // 10 seconds for warnings

    // Check if enough time has passed since last alert of this type
    if (now - lastAlertTime < minAlertInterval) {
      return;
    }

    // Update last alert time
    this.lastAlertTimes.set(lastAlertKey, now);

    const alertMessage = metric === 'frameRate' ?
      `${metric} (${value.toFixed(2)}) fell below ${triggeredThreshold.severity} threshold (${triggeredThreshold.threshold})` :
      `${metric} (${value.toFixed(2)}) exceeded ${triggeredThreshold.severity} threshold (${triggeredThreshold.threshold})`;

    const alertId = `${metric}_${triggeredThreshold.severity}_${Date.now()}`;
    const alert: PerformanceAlert = {
      id: alertId,
      message: alertMessage,
      severity: triggeredThreshold.severity,
      timestamp: Date.now(),
      metric,
      value,
      threshold: triggeredThreshold.threshold,
      wallId,
      operationType
    };

    this.alerts.set(alertId, alert);

    // Call threshold callback
    triggeredThreshold.callback?.(value, triggeredThreshold.threshold);

    // Notify alert subscribers
    this.alertCallbacks.forEach(callback => callback(alert));

    // Log alert
    console.warn('PerformanceMonitor: Performance alert', alert);
  }

  subscribeToAlerts(callback: (alert: PerformanceAlert) => void): () => void {
    this.alertCallbacks.add(callback);
    return () => this.alertCallbacks.delete(callback);
  }

  analyzePerformance(): object {
    if (!this.isEnabled) return {};

    const analysis = {
      timestamp: Date.now(),

      // Timing analysis
      timing: {
        averageFrameRate: this.metrics.averageFrameTime,
        frameRateVariability: this.calculateVariability(this.metrics.frameRateHistory),
        operationDurations: this.analyzeOperationDurations(),
        slowestOperations: this.getSlowdestOperations(10)
      },

      // Memory analysis
      memory: {
        currentUsage: performance.memory ? performance.memory.usedJSHeapSize / (1024 * 1024) : 0,
        memoryTrend: this.calculateTrend(this.metrics.memoryUsageHistory),
        memoryLeakRisk: this.assessMemoryLeakRisk(),
        garbageCollectionImpact: this.metrics.garbageCollectionImpact.length > 0 ?
          this.metrics.garbageCollectionImpact.reduce((a, b) => a + b, 0) / this.metrics.garbageCollectionImpact.length : 0
      },

      // Error analysis
      errors: {
        totalErrors: Array.from(this.metrics.errorRates.values()).reduce((sum, err) => sum + err.count, 0),
        errorRateByType: Object.fromEntries(this.metrics.errorRates),
        criticalErrors: this.metrics.criticalErrors,
        averageRecoveryTime: this.metrics.errorRecoveryTimes.length > 0 ?
          this.metrics.errorRecoveryTimes.reduce((a, b) => a + b, 0) / this.metrics.errorRecoveryTimes.length : 0
      },

      // User experience analysis
      userExperience: {
        responsivenessFactor: this.metrics.responsivenessFactor,
        visualGlitches: this.metrics.visualGlitches,
        performanceDegradation: this.metrics.performanceDegradation,
        overallScore: this.calculateOverallPerformanceScore()
      },

      // Active operations
      activeOperations: Array.from(this.activeOperations).map(id => {
        const operation = this.dragOperations.get(id);
        return operation ? {
          id,
          wallId: operation.wallId,
          operationType: operation.operationType,
          duration: performance.now() - operation.startTime,
          eventCount: operation.events.length,
          errorCount: operation.errorCount
        } : null;
      }).filter(Boolean)
    };

    // Store analysis in history
    this.analysisHistory.push({ timestamp: Date.now(), analysis });

    // Keep only last 50 analyses
    if (this.analysisHistory.length > 50) {
      this.analysisHistory.shift();
    }

    return analysis;
  }

  detectBottlenecks(): string[] {
    const bottlenecks: string[] = [];

    // Check for slow operations
    const slowOperations = this.getSlowdestOperations(5);
    if (slowOperations.length > 0) {
      bottlenecks.push(`Slow operations detected: ${slowOperations.map(op => `${op.type} (${op.avgDuration.toFixed(2)}ms)`).join(', ')}`);
    }

    // Check for memory issues
    const memoryTrend = this.calculateTrend(this.metrics.memoryUsageHistory);
    if (memoryTrend > 1.5) {
      bottlenecks.push('Memory usage is consistently increasing, potential memory leak');
    }

    // Check for high error rates
    for (const [errorType, errorData] of this.metrics.errorRates) {
      const errorRate = (errorData.count / errorData.total) * 100;
      if (errorRate > 10) {
        bottlenecks.push(`High error rate for ${errorType}: ${errorRate.toFixed(1)}%`);
      }
    }

    // Check for low frame rates
    if (this.metrics.averageFrameTime < 20) {
      bottlenecks.push(`Low frame rate: ${this.metrics.averageFrameTime.toFixed(1)} FPS`);
    }

    // Check for active alerts
    const criticalAlerts = Array.from(this.alerts.values()).filter(alert => alert.severity === 'critical');
    if (criticalAlerts.length > 0) {
      bottlenecks.push(`${criticalAlerts.length} critical performance alerts active`);
    }

    return bottlenecks;
  }

  getPerformanceTrends(): object {
    if (this.analysisHistory.length < 2) {
      return { message: 'Insufficient data for trend analysis' };
    }

    const recent = this.analysisHistory.slice(-10);

    return {
      frameRate: this.calculateTrendFromAnalysis(recent, 'timing.averageFrameRate'),
      memoryUsage: this.calculateTrendFromAnalysis(recent, 'memory.currentUsage'),
      errorRate: this.calculateTrendFromAnalysis(recent, 'errors.totalErrors'),
      responseTime: this.calculateTrendFromAnalysis(recent, 'userExperience.responsivenessFactor'),
      overallPerformance: this.calculateTrendFromAnalysis(recent, 'userExperience.overallScore')
    };
  }

  generatePerformanceReport(): string {
    const analysis = this.analyzePerformance();
    const bottlenecks = this.detectBottlenecks();
    const trends = this.getPerformanceTrends();

    let report = 'PERFORMANCE MONITOR REPORT\n';
    report += '==========================\n\n';

    report += `Generated: ${new Date().toISOString()}\n`;
    report += `Monitoring Enabled: ${this.isEnabled}\n`;
    report += `Active Operations: ${this.activeOperations.size}\n\n`;

    // Performance metrics
    report += 'PERFORMANCE METRICS:\n';
    report += `- Average Frame Rate: ${(analysis as any).timing?.averageFrameRate?.toFixed(1) || 'N/A'} FPS\n`;
    report += `- Memory Usage: ${(analysis as any).memory?.currentUsage?.toFixed(1) || 'N/A'} MB\n`;
    report += `- Total Errors: ${(analysis as any).errors?.totalErrors || 0}\n`;
    report += `- Critical Errors: ${(analysis as any).errors?.criticalErrors || 0}\n`;
    report += `- Overall Score: ${(analysis as any).userExperience?.overallScore?.toFixed(2) || 'N/A'}/100\n\n`;

    // Bottlenecks
    if (bottlenecks.length > 0) {
      report += 'BOTTLENECKS DETECTED:\n';
      bottlenecks.forEach((bottleneck, index) => {
        report += `${index + 1}. ${bottleneck}\n`;
      });
      report += '\n';
    }

    // Trends
    report += 'PERFORMANCE TRENDS:\n';
    report += `- Frame Rate: ${this.describeTrend((trends as any).frameRate)}\n`;
    report += `- Memory Usage: ${this.describeTrend((trends as any).memoryUsage)}\n`;
    report += `- Error Rate: ${this.describeTrend((trends as any).errorRate)}\n\n`;

    // Recommendations
    report += 'RECOMMENDATIONS:\n';
    const recommendations = this.generateRecommendations(analysis, bottlenecks);
    recommendations.forEach((rec, index) => {
      report += `${index + 1}. ${rec}\n`;
    });

    return report;
  }

  private analyzeOperationDurations(): object {
    const analysis: { [key: string]: { count: number; avgDuration: number; maxDuration: number } } = {};

    for (const [operationType, durations] of this.metrics.operationDurations) {
      analysis[operationType] = {
        count: durations.length,
        avgDuration: durations.reduce((a, b) => a + b, 0) / durations.length,
        maxDuration: Math.max(...durations)
      };
    }

    return analysis;
  }

  private getSlowdestOperations(count: number): Array<{ type: string; avgDuration: number; maxDuration: number }> {
    const operations = [];

    for (const [operationType, durations] of this.metrics.operationDurations) {
      const avgDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
      const maxDuration = Math.max(...durations);

      operations.push({ type: operationType, avgDuration, maxDuration });
    }

    return operations.sort((a, b) => b.avgDuration - a.avgDuration).slice(0, count);
  }

  private calculateTrend(values: number[]): number {
    if (values.length < 2) return 0;

    const firstHalf = values.slice(0, Math.floor(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));

    const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;

    return secondAvg / firstAvg;
  }

  private calculateVariability(values: number[]): number {
    if (values.length < 2) return 0;

    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / values.length;

    return Math.sqrt(variance);
  }

  private assessMemoryLeakRisk(): 'low' | 'medium' | 'high' {
    if (this.metrics.memoryLeakIndicators.length === 0) return 'low';

    const recentLeaks = this.metrics.memoryLeakIndicators.filter(
      indicator => Date.now() - indicator.timestamp < 300000 // Last 5 minutes
    );

    if (recentLeaks.length >= 3) return 'high';
    if (recentLeaks.length >= 1) return 'medium';
    return 'low';
  }

  private calculateOverallPerformanceScore(): number {
    let score = 100;

    // Frame rate impact (30% of score)
    if (this.metrics.averageFrameTime < 60) {
      score -= (60 - this.metrics.averageFrameTime) * 0.5;
    }

    // Error rate impact (25% of score)
    const totalErrors = Array.from(this.metrics.errorRates.values()).reduce((sum, err) => sum + err.count, 0);
    const totalOperations = Array.from(this.metrics.errorRates.values()).reduce((sum, err) => sum + err.total, 0);
    const errorRate = totalOperations > 0 ? (totalErrors / totalOperations) * 100 : 0;
    score -= errorRate * 2;

    // Memory usage impact (20% of score)
    if (performance.memory) {
      const memoryUsageMB = performance.memory.usedJSHeapSize / (1024 * 1024);
      if (memoryUsageMB > 100) {
        score -= (memoryUsageMB - 100) * 0.2;
      }
    }

    // Critical errors impact (15% of score)
    score -= this.metrics.criticalErrors * 5;

    // Visual glitches impact (10% of score)
    score -= this.metrics.visualGlitches * 2;

    return Math.max(0, Math.min(100, score));
  }

  private calculateTrendFromAnalysis(analyses: Array<{ timestamp: number; analysis: any }>, path: string): number {
    const values = analyses.map(a => this.getNestedValue(a.analysis, path)).filter(v => v !== undefined);
    return this.calculateTrend(values);
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  private describeTrend(trend: number): string {
    if (trend > 1.1) return 'Improving';
    if (trend < 0.9) return 'Degrading';
    return 'Stable';
  }

  private generateRecommendations(analysis: any, bottlenecks: string[]): string[] {
    const recommendations: string[] = [];

    // Frame rate recommendations
    if (analysis.timing?.averageFrameRate < 30) {
      recommendations.push('Consider reducing complexity of geometry operations or implementing progressive rendering');
    }

    // Memory recommendations
    if (analysis.memory?.memoryTrend > 1.2) {
      recommendations.push('Implement memory cleanup for geometry caches and dispose unused Three.js resources');
    }

    // Error recommendations
    if (analysis.errors?.totalErrors > 10) {
      recommendations.push('Add more robust error handling and validation before polygon operations');
    }

    // Active operations recommendations
    if (analysis.activeOperations?.length > 5) {
      recommendations.push('Consider limiting concurrent drag operations or implementing operation queuing');
    }

    // General recommendations based on bottlenecks
    if (bottlenecks.some(b => b.includes('memory'))) {
      recommendations.push('Enable progressive mode for complex operations and implement geometry caching');
    }

    if (bottlenecks.some(b => b.includes('error rate'))) {
      recommendations.push('Implement pre-validation for polygon operations and add error recovery mechanisms');
    }

    return recommendations;
  }

  // Utility methods
  reset(): void {
    this.disableMonitoring();
    this.dragOperations.clear();
    this.activeOperations.clear();
    this.alerts.clear();
    this.metrics = {
      operationDurations: new Map(),
      averageFrameTime: 0,
      frameRateHistory: [],
      updateFrequencies: new Map(),
      memoryUsageHistory: [],
      memoryLeakIndicators: [],
      garbageCollectionImpact: [],
      errorRates: new Map(),
      errorRecoveryTimes: [],
      criticalErrors: 0,
      responsivenessFactor: 1.0,
      visualGlitches: 0,
      performanceDegradation: 0
    };
    this.analysisHistory = [];
  }

  enable(): void {
    this.isEnabled = true;
    this.startPerformanceTracking();
  }

  disable(): void {
    this.disableMonitoring();
  }

  // Force stop all tracking immediately
  forceStop(): void {
    this.isEnabled = false;
    this.isTracking = false;
    if (this.memoryTrackingTimeout !== null) {
      clearTimeout(this.memoryTrackingTimeout);
      this.memoryTrackingTimeout = null;
    }
  }

  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  getAlerts(): PerformanceAlert[] {
    return Array.from(this.alerts.values());
  }

  clearAlerts(): void {
    this.alerts.clear();
  }
}

// Export singleton instance
export default PerformanceMonitor.getInstance();
